import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import api from '../services/api';
import Logo from './Logo';
import './Navbar.css';

const Navbar: React.FC = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [showUserMenu, setShowUserMenu] = useState<boolean>(false);
  const [showMobileMenu, setShowMobileMenu] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [cartItemCount, setCartItemCount] = useState<number>(0);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Extract search query from URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get('q') || '';
    setSearchTerm(q);
  }, [location.search]);

  // Fetch cart item count
  useEffect(() => {
    const fetchCartCount = async (): Promise<void> => {
      try {
        const response = await api.get<Array<{ id: number; quantity: number }>>('/cart');
        const totalItems = response.data.reduce((sum, item) => sum + item.quantity, 0);
        setCartItemCount(totalItems);
      } catch (error) {
        // Silently fail - cart count is non-critical
        setCartItemCount(0);
      }
    };
    fetchCartCount();
    // Refresh cart count when navigating (in case items were added/removed)
    const interval = setInterval(fetchCartCount, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, [location.pathname]);

  // Debug: Log user role to help troubleshoot
  useEffect(() => {
    if (isAuthenticated && user) {
      console.log('Navbar - User object:', user);
      console.log('Navbar - User role:', user.role);
      console.log('Navbar - Role type:', typeof user.role);
      console.log('Navbar - Role value:', JSON.stringify(user.role));
      console.log('Navbar - Is Admin (exact)?', user.role === 'Admin');
      console.log('Navbar - Is Admin (lowercase)?', user.role && user.role.toLowerCase() === 'admin');
    }
  }, [user, isAuthenticated]);

  const handleLogout = (): void => {
    logout();
    navigate('/');
    setShowUserMenu(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  return (
    <nav className="navbar">
      <div className="navbar-container">
        <Link to="/" className="navbar-brand">
          <Logo className="phi-logo-img" />
          <span className="brand-name">PHI PRINTING</span>
        </Link>
        
        {/* Desktop Menu */}
        <div className="navbar-menu desktop-menu">
          <div className="nav-links">
            <Link 
              to="/" 
              className={`navbar-link ${location.pathname === '/' ? 'active' : ''}`}
            >
              Home
            </Link>
            <Link 
              to="/art" 
              className={`navbar-link ${location.pathname.startsWith('/art') ? 'active' : ''}`}
            >
              Gallery
            </Link>
            <Link 
              to="/create-print" 
              className={`navbar-link ${location.pathname === '/create-print' ? 'active' : ''}`}
            >
              Create Print
            </Link>
            <Link 
              to="/become-an-artist" 
              className={`navbar-link ${location.pathname === '/become-an-artist' ? 'active' : ''}`}
            >
              Become an Artist
            </Link>
          </div>
        </div>

        <form 
          className="navbar-search desktop-menu"
          onSubmit={(e) => {
            e.preventDefault();
            const formData = new FormData(e.currentTarget);
            const query = (formData.get('search') as string)?.trim();
            if (query) {
              navigate(`/art/browse?q=${encodeURIComponent(query)}`);
            } else {
              navigate('/art');
            }
          }}
        >
          <input
            type="text"
            name="search"
            placeholder="Search artworks, artists..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="navbar-search-input"
          />
          <button type="submit" className="navbar-search-button" aria-label="Search">
            <i className="fas fa-search"></i>
          </button>
        </form>
          
        <div className="navbar-actions desktop-menu">
            <Link to="/cart" className="cart-icon-link" title="Shopping Cart">
              <i className="fas fa-shopping-cart"></i>
              <span className="cart-badge">
                {cartItemCount > 0 ? `Cart (${cartItemCount})` : 'Cart'}
              </span>
            </Link>
            
            <button 
              onClick={toggleTheme} 
              className="theme-toggle"
              aria-label="Toggle theme"
              title={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
            >
              <i className={theme === 'light' ? 'fas fa-moon' : 'fas fa-sun'}></i>
            </button>

            {isAuthenticated ? (
              <div className="user-menu" ref={userMenuRef}>
                <button
                  className="user-menu-button"
                  onClick={() => setShowUserMenu(!showUserMenu)}
                >
                  <i className="fas fa-user"></i>
                  <span>{user?.displayName || [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email}</span>
                  <i className={`fas fa-chevron-${showUserMenu ? 'up' : 'down'}`}></i>
                </button>
                
                {showUserMenu && (
                  <div className="user-menu-dropdown">
                    <Link to="/history" className="user-menu-item" onClick={() => setShowUserMenu(false)}>
                      <i className="fas fa-history"></i> Purchase History
                    </Link>
                    <Link to="/profile" className="user-menu-item" onClick={() => setShowUserMenu(false)}>
                      <i className="fas fa-user-circle"></i> Profile
                    </Link>
                    {user?.role === 'Artist' && (
                      <Link to="/artist/metrics" className="user-menu-item" onClick={() => setShowUserMenu(false)}>
                        <i className="fas fa-chart-line"></i> Artist Metrics
                      </Link>
                    )}
                    <Link to="/addresses" className="user-menu-item" onClick={() => setShowUserMenu(false)}>
                      <i className="fas fa-address-book"></i> Addresses
                    </Link>
                    <Link to="/returns" className="user-menu-item" onClick={() => setShowUserMenu(false)}>
                      <i className="fas fa-undo"></i> Returns
                    </Link>
                    {user?.role === 'Admin' && (
                      <Link to="/admin" className="user-menu-item" onClick={() => setShowUserMenu(false)}>
                        <i className="fas fa-cog"></i> Admin Dashboard
                      </Link>
                    )}
                    <button className="user-menu-item user-menu-logout" onClick={handleLogout}>
                      <i className="fas fa-sign-out-alt"></i> Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/login" className="btn btn-primary btn-small">
                Login
              </Link>
            )}
        </div>

        {/* Mobile Menu Toggle */}
        <button
          className="mobile-menu-toggle"
          onClick={() => setShowMobileMenu(!showMobileMenu)}
          aria-label="Toggle menu"
        >
          <i className={`fas fa-${showMobileMenu ? 'times' : 'bars'}`}></i>
        </button>
      </div>

      {/* Mobile Menu */}
      {showMobileMenu && (
        <div className="mobile-menu">
          <Link to="/" onClick={() => setShowMobileMenu(false)}>Home</Link>
          <Link to="/art" onClick={() => setShowMobileMenu(false)}>Gallery</Link>
          <Link to="/create-print" onClick={() => setShowMobileMenu(false)}>Create Print</Link>
          <Link to="/become-an-artist" onClick={() => setShowMobileMenu(false)}>Become an Artist</Link>
          <Link to="/cart" onClick={() => setShowMobileMenu(false)}>Cart</Link>
          {isAuthenticated ? (
            <>
              <Link to="/history" onClick={() => setShowMobileMenu(false)}>Purchase History</Link>
              <Link to="/profile" onClick={() => setShowMobileMenu(false)}>Profile</Link>
              {user?.role === 'Artist' && (
                <Link to="/artist/metrics" onClick={() => setShowMobileMenu(false)}>Artist Metrics</Link>
              )}
              <Link to="/addresses" onClick={() => setShowMobileMenu(false)}>Addresses</Link>
              <Link to="/returns" onClick={() => setShowMobileMenu(false)}>Returns</Link>
              {user?.role === 'Admin' && (
                <Link to="/admin" onClick={() => setShowMobileMenu(false)}>Admin Dashboard</Link>
              )}
              <button onClick={handleLogout}>Logout</button>
            </>
          ) : (
            <Link to="/login" onClick={() => setShowMobileMenu(false)}>Login</Link>
          )}
        </div>
      )}
    </nav>
  );
};

export default Navbar;

